import { useState } from 'react';
import { FriendService } from '@/api/services/friendService';
import {
  AddFriendParams,
  RemoveFriendParams,
  BlockFriendParams,
  UnblockFriendParams,
  ReportFriendParams,
  UpdateFriendParams
} from '@/api/types';
import { logPrint } from '@/utils/my-utils';

/**
 * 好友操作相关的 hook
 * 处理添加、移除、屏蔽等一次性操作
 */
export function useFriendActions() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * 执行好友操作的通用方法
   * @param operation 操作函数
   * @param params 操作参数
   * @param successMessage 成功提示信息
   * @returns Promise<boolean> 操作是否成功
   */
  const executeOperation = async <T>(
    operation: (params: T) => Promise<void>,
    params: T,
    successMessage: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError('');

    try {
      await operation(params);
      logPrint(successMessage);
      return true;
    } catch (err: any) {
      console.error('好友操作失败:', err);
      const errorMessage = err?.err_msg || '操作失败，请重试';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 添加好友
   * @param params 添加好友参数
   * @returns Promise<boolean> 是否添加成功
   */
  const addFriend = async (params: AddFriendParams): Promise<boolean> => {
    return executeOperation(
      FriendService.addFriend,
      params,
      '好友添加成功'
    );
  };

  /**
   * 移除好友
   * @param params 移除好友参数
   * @returns Promise<boolean> 是否移除成功
   */
  const removeFriend = async (params: RemoveFriendParams): Promise<boolean> => {
    return executeOperation(
      FriendService.removeFriend,
      params,
      '好友移除成功'
    );
  };

  /**
   * 屏蔽用户
   * @param params 屏蔽用户参数
   * @returns Promise<boolean> 是否屏蔽成功
   */
  const blockFriend = async (params: BlockFriendParams): Promise<boolean> => {
    return executeOperation(
      FriendService.blockFriend,
      params,
      '用户屏蔽成功'
    );
  };

  /**
   * 取消屏蔽用户
   * @param params 取消屏蔽参数
   * @returns Promise<boolean> 是否取消屏蔽成功
   */
  const unblockFriend = async (params: UnblockFriendParams): Promise<boolean> => {
    return executeOperation(
      FriendService.unblockFriend,
      params,
      '取消屏蔽成功'
    );
  };

  /**
   * 举报用户
   * @param params 举报参数
   * @returns Promise<boolean> 是否举报成功
   */
  const reportFriend = async (params: ReportFriendParams): Promise<boolean> => {
    return executeOperation(
      FriendService.reportFriend,
      params,
      '举报提交成功'
    );
  };

  /**
   * 更新好友信息
   * @param params 更新参数
   * @returns Promise<boolean> 是否更新成功
   */
  const updateFriend = async (params: UpdateFriendParams): Promise<boolean> => {
    return executeOperation(
      FriendService.updateFriend,
      params,
      '好友信息更新成功'
    );
  };

  /**
   * 清除错误信息
   */
  const clearError = () => {
    setError('');
  };

  return {
    // 状态
    isLoading,
    error,

    // 操作方法
    addFriend,
    removeFriend,
    blockFriend,
    unblockFriend,
    reportFriend,
    updateFriend,

    // 工具方法
    clearError
  };
}